import React, { useState } from 'react';
import { ChevronDown, Shield, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const LAST_UPDATED = 'June 2025';
const EFFECTIVE_DATE = 'June 2025';
const CONTACT_EMAIL = 'support@utajirihub.com';

const sections = [
  {
    id: 1,
    title: 'Introduction',
    content: `UtajiriHub is a personal wealth tracking application that helps you measure and grow your net worth. We take your privacy seriously and are committed to being transparent about what data we collect and how we use it.

By using UtajiriHub, you agree to the practices described in this policy. If you do not agree, please stop using the app.`
  },
  {
    id: 2,
    title: 'Who We Are',
    content: `UtajiriHub is an independently operated personal finance application available on Google Play, based in Kenya.

For privacy-related questions or requests, contact us at: ${CONTACT_EMAIL}`
  },
  {
    id: 3,
    title: 'Information We Collect',
    content: `We only collect what is necessary to run the app.

Account information (required for sign-in):
• Your email address

This is the only personal information we collect. It is used solely to authenticate your account. We do not use it for marketing without your consent.

Financial data (entirely voluntary):
All financial information — including investment amounts, asset types, loan balances, wealth goals, and net worth figures — is entered by you voluntarily. You are in full control of what you add, edit, or delete.

Usage data (collected automatically):
• Device type and operating system version
• App version and crash/error reports
• General usage patterns (e.g. which features are used, session duration)

This data is anonymised and used only to improve the app's stability and performance.

What we do NOT collect:
• Bank account numbers or payment credentials
• Government-issued identification
• Biometric data
• Location data
• Contacts or social media profiles
• Any data not directly related to the app's core functionality`
  },
  {
    id: 4,
    title: 'How We Use Your Information',
    content: `We use your information only to operate and improve UtajiriHub:

• To authenticate your account and keep your session secure
• To display your net worth, assets, liabilities, and goal progress
• To restore your data across sessions and devices
• To diagnose technical issues and improve app stability
• To send important service-related communications (e.g. security notices)

We do not use your data for advertising, credit assessment, profiling, or any automated decision-making that affects you.`
  },
  {
    id: 5,
    title: 'How We Share Your Information',
    content: `We do not sell, rent, or share your personal or financial data with third parties for marketing or commercial purposes. Ever.

Limited sharing occurs only in these circumstances:

• Service providers: We use trusted third-party services (e.g. cloud hosting, crash analytics) to operate the app. These providers are contractually required to handle your data securely and may not use it for their own purposes.

• Legal compliance: We may disclose data if required by Kenyan law, a valid court order, or a governmental authority.

• Business transfers: If UtajiriHub is acquired or merges with another entity, your data may transfer to the new owner. You will be notified in advance, and the new owner must honour this policy or notify you of any changes.`
  },
  {
    id: 6,
    title: 'Data Storage and Security',
    content: `Your data is stored on secure servers with encryption in transit (HTTPS/TLS) and at rest. We apply reasonable industry-standard security practices to prevent unauthorised access, disclosure, or modification.

No system is 100% secure. While we take reasonable precautions, we cannot guarantee absolute security of data transmitted over the internet.

If you ever believe your account has been compromised, contact us immediately at ${CONTACT_EMAIL}.`
  },
  {
    id: 7,
    title: 'Data Retention',
    content: `We keep your data for as long as your account is active or as needed to provide the service.

If you delete your account, we will delete or anonymise all associated personal data within 30 days, unless we are legally required to retain it for longer.

You can request account deletion at any time by contacting us at ${CONTACT_EMAIL}.`
  },
  {
    id: 8,
    title: 'Your Rights',
    content: `Under the Kenya Data Protection Act 2019, you have the right to:

• Access: Request a copy of the personal data we hold about you
• Correction: Ask us to correct inaccurate or incomplete information
• Deletion: Request that we delete your account and associated data
• Portability: Receive your data in a structured, machine-readable format
• Objection: Object to certain types of processing, such as analytics

To exercise any of these rights, email us at ${CONTACT_EMAIL}. We will respond within 30 days.`
  },
  {
    id: 9,
    title: "Children's Privacy",
    highlight: 'Parental consent required for users under 18',
    content: `UtajiriHub is available to users of all ages, including those under 18.

In compliance with the Kenya Data Protection Act 2019, we require verifiable parental or guardian consent before we knowingly collect or process personal data from any user under the age of 18.

If you are under 18, please ask a parent or guardian to review this Privacy Policy with you and give their consent before you create an account or enter any personal information into the app.

Parents and guardians may contact us at ${CONTACT_EMAIL} to review, request correction of, or request deletion of their child's data. We will act on such requests promptly.

If we discover we have collected data from a minor without verifiable parental consent, we will delete that data as soon as possible.`
  },
  {
    id: 10,
    title: 'Third-Party Links and Services',
    content: `The Acquire Assets section and other parts of the app may link to external websites or investment platforms. UtajiriHub is not responsible for the privacy practices or content of those third-party sites.

We encourage you to review the privacy policy of any external service you access through the app before providing them with any personal information.`
  },
  {
    id: 11,
    title: 'Analytics and Tracking',
    content: `We use privacy-respecting analytics tools to understand how the app is used. These tools operate on aggregated, anonymised data that does not personally identify you.

We do not use advertising trackers, third-party cookies, or cross-app tracking technologies of any kind.`
  },
  {
    id: 12,
    title: 'Changes to This Policy',
    content: `We may update this Privacy Policy from time to time. When we do, we will update the "Last Updated" date at the top of this page and notify you through the app or via email if the changes are significant.

Your continued use of UtajiriHub after any update means you accept the revised policy. If you disagree with changes, you may delete your account at any time.`
  },
  {
    id: 13,
    title: 'Governing Law',
    content: `This Privacy Policy is governed by the laws of Kenya, including the Data Protection Act (No. 24 of 2019) and any regulations made under it.

If you have a complaint about how we handle your data, please contact us first at ${CONTACT_EMAIL} and we will work with you to resolve it in good faith.`
  },
  {
    id: 14,
    title: 'Contact Us',
    content: `For any questions, concerns, or requests related to this Privacy Policy or your personal data, please reach us at:

Email: ${CONTACT_EMAIL}
App: UtajiriHub (Google Play)
Country of operation: Kenya`
  }
];

function AccordionItem({ section, isOpen, onToggle }) {
  return (
    <div className="border border-[#2a3942] rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-5 py-4 bg-[#1f2c34] hover:bg-[#243340] transition-colors text-left"
        aria-expanded={isOpen}
      >
        <div className="flex items-center gap-3">
          <span className="text-[#00a884] text-xs font-mono font-medium w-6 shrink-0">
            {String(section.id).padStart(2, '0')}
          </span>
          <span className="text-white text-sm font-medium">{section.title}</span>
          {section.highlight && (
            <span className="hidden sm:inline-block text-[10px] bg-amber-500/10 text-amber-400 border border-amber-500/20 rounded-full px-2 py-0.5">
              {section.highlight}
            </span>
          )}
        </div>
        <ChevronDown
          className={`w-4 h-4 text-[#8696a0] shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="px-5 py-4 bg-[#0d1b22] border-t border-[#2a3942]">
          {section.highlight && (
            <div className="mb-3 pl-9">
              <span className="inline-block text-xs bg-amber-500/10 text-amber-400 border border-amber-500/20 rounded-full px-3 py-1">
                ⚠ {section.highlight}
              </span>
            </div>
          )}
          <div className="text-[#8696a0] text-sm leading-relaxed whitespace-pre-line pl-9">
            {section.content}
          </div>
        </div>
      )}
    </div>
  );
}

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  const [openSection, setOpenSection] = useState(null);

  const toggleSection = (id) => {
    setOpenSection(openSection === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-[#0b141a]">
      {/* Header */}
      <div className="bg-[#1f2c34] border-b border-[#2a3942] px-4 py-4 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="text-[#8696a0] hover:text-white transition-colors p-1 -ml-1"
            aria-label="Go back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <span className="text-white font-medium">Privacy Policy</span>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Hero */}
        <div className="flex flex-col items-center text-center mb-10">
          <div className="w-14 h-14 rounded-full bg-[#00a884]/10 flex items-center justify-center mb-4">
            <Shield className="w-7 h-7 text-[#00a884]" />
          </div>
          <h1 className="text-white text-2xl font-medium mb-1">Your Privacy Matters</h1>
          <p className="text-[#8696a0] text-sm max-w-sm">
            Only your email address is collected for sign-in — all financial data is yours alone.
          </p>
          <div className="flex gap-4 mt-4 text-xs text-[#8696a0]">
            <span>Effective: {EFFECTIVE_DATE}</span>
            <span>·</span>
            <span>Last updated: {LAST_UPDATED}</span>
          </div>
        </div>

        {/* Summary pills */}
        <div className="grid grid-cols-3 gap-3 mb-8">
          {[
            { label: 'No ads', icon: '🚫' },
            { label: 'No data selling', icon: '🔒' },
            { label: 'No ads', icon: '🚫'},
          ].map((item) => (
            <div
              key={item.label}
              className="bg-[#1f2c34] border border-[#2a3942] rounded-lg p-3 text-center"
            >
              <div className="text-lg mb-1">{item.icon}</div>
              <div className="text-[#8696a0] text-xs">{item.label}</div>
            </div>
          ))}
        </div>

        {/* What we actually collect — plain language summary */}
        <div className="bg-[#1f2c34] border border-[#2a3942] rounded-lg p-4 mb-6">
          <p className="text-[#00a884] text-xs font-medium uppercase tracking-wide mb-3">Plain language summary</p>
          <div className="space-y-2">
            {[
              { label: 'Email address', reason: 'Required — used only for sign-in / authentication' },
              { label: 'Financial data you enter', reason: 'Voluntary — assets, loans, goals you add yourself' },
              { label: 'Crash reports & usage stats', reason: 'Automatic — anonymised, used to fix bugs' },
            ].map(({ label, reason }) => (
              <div key={label} className="flex items-start gap-3">
                <span className="text-[#00a884] text-xs mt-0.5">✓</span>
                <div>
                  <span className="text-white text-sm">{label}</span>
                  <span className="text-[#8696a0] text-xs ml-2">— {reason}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Accordion */}
        <div className="space-y-2">
          {sections.map((section) => (
            <AccordionItem
              key={section.id}
              section={section}
              isOpen={openSection === section.id}
              onToggle={() => toggleSection(section.id)}
            />
          ))}
        </div>

        {/* Footer */}
        <div className="mt-10 pt-6 border-t border-[#2a3942] text-center">
          <p className="text-[#8696a0] text-xs mb-2">Questions about your privacy?</p>
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="text-[#00a884] text-sm hover:underline"
          >
            {CONTACT_EMAIL}
          </a>
          <p className="text-[#2a3942] text-xs mt-6">
            © {new Date().getFullYear()} UtajiriHub. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}